import { useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, FileDown, Printer, CheckCircle2, ChevronLeft, Info } from "lucide-react";
import { COMPARTMENT } from "../../lib/vault/vaultService";

/**
 * EmergencyCardExport — device-generated offline HTML emergency card.
 * User picks fields, we build a self-contained HTML file and either download
 * it (they can AirDrop / email / save to Files) or open a print dialog for
 * PDF-to-glovebox printing. Zero server involvement.
 */

const FIELDS = [
  { k: "fullName",        l: "Full name",              tint: "slate" },
  { k: "dateOfBirth",     l: "Date of birth",          tint: "slate" },
  { k: "bloodType",       l: "Blood type",             tint: "rose" },
  { k: "primaryLanguage", l: "Preferred language",     tint: "slate" },
  { k: "organDonor",      l: "Organ donor status",     tint: "emerald" },
  { k: "allergies",       l: "Allergies",              tint: "rose" },
  { k: "medicalConditions", l: "Medical conditions",   tint: "rose" },
  { k: "medications",     l: "Current medications",    tint: "amber" },
  { k: "primaryContact",  l: "Primary emergency contact", tint: "emerald" },
  { k: "insurance",       l: "Insurance provider + policy #", tint: "sky" },
  { k: "providers",       l: "Physicians",             tint: "sky" },
  { k: "emergencyInstructions", l: "Emergency instructions", tint: "rose" },
];

function esc(s) {
  return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function buildCardHtml(data, picked) {
  const p = data.personalInfo || {};
  const primary = (data.emergencyContacts || []).find((c) => c.isPrimary) || (data.emergencyContacts || [])[0];
  const now = new Date().toLocaleString();
  const sections = [];
  const block = (label, body) => `<div class="row"><div class="label">${esc(label)}</div><div class="value">${body}</div></div>`;

  if (picked.has("fullName")        && p.fullName)        sections.push(block("Name", `<strong>${esc(p.fullName)}</strong>`));
  if (picked.has("dateOfBirth")     && p.dateOfBirth)     sections.push(block("Date of birth", esc(p.dateOfBirth)));
  if (picked.has("bloodType")       && p.bloodType)       sections.push(block("Blood type", `<span class="pill rose">${esc(p.bloodType)}</span>`));
  if (picked.has("primaryLanguage") && p.primaryLanguage) sections.push(block("Language", esc(p.primaryLanguage)));
  if (picked.has("organDonor"))                           sections.push(block("Organ donor", p.organDonor ? "Yes" : "No"));

  if (picked.has("allergies") && (data.allergies || []).length) {
    sections.push(block("Allergies", (data.allergies || []).map((a) => `<span class="pill rose">${esc(a)}</span>`).join(" ")));
  }
  if (picked.has("medicalConditions") && (data.medicalConditions || []).length) {
    sections.push(block("Conditions", (data.medicalConditions || []).map((c) => `<span class="pill amber">${esc(c)}</span>`).join(" ")));
  }
  if (picked.has("medications") && (data.medications || []).length) {
    const rows = (data.medications || []).filter((m) => m.isActive !== false)
      .map((m) => `<li>${esc(m.name)}${m.dosage ? ` · <em>${esc(m.dosage)}</em>` : ""}${m.frequency ? ` · ${esc(m.frequency)}` : ""}</li>`)
      .join("");
    sections.push(block("Medications", `<ul>${rows}</ul>`));
  }
  if (picked.has("primaryContact") && primary) {
    sections.push(block("Emergency contact",
      `<div><strong>${esc(primary.name)}</strong> · ${esc(primary.relationship)}</div>
       <div><a href="tel:${esc(primary.phone)}">${esc(primary.phone)}</a>${primary.alternatePhone ? ` · <a href="tel:${esc(primary.alternatePhone)}">${esc(primary.alternatePhone)}</a>` : ""}</div>`));
  }
  if (picked.has("insurance") && data.insurance?.providerName) {
    sections.push(block("Insurance", `${esc(data.insurance.providerName)}${data.insurance.policyNumber ? ` · <span class="mono">${esc(data.insurance.policyNumber)}</span>` : ""}${data.insurance.phone ? ` · <a href="tel:${esc(data.insurance.phone)}">${esc(data.insurance.phone)}</a>` : ""}`));
  }
  if (picked.has("providers") && (data.providers || []).length) {
    const rows = (data.providers || []).map((v) => `<li><strong>${esc(v.name)}</strong>${v.specialty ? ` (${esc(v.specialty)})` : ""}${v.phone ? ` · <a href="tel:${esc(v.phone)}">${esc(v.phone)}</a>` : ""}</li>`).join("");
    sections.push(block("Physicians", `<ul>${rows}</ul>`));
  }
  if (picked.has("emergencyInstructions") && data.emergencyInstructions) {
    sections.push(block("Instructions", `<p class="instr">${esc(data.emergencyInstructions)}</p>`));
  }

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Emergency Card · ${esc(p.fullName || "Traveler")}</title>
<style>
  *{box-sizing:border-box}
  body{margin:0;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,Arial,sans-serif;background:#fef2f2;color:#0f172a;line-height:1.4;padding:24px}
  .card{max-width:640px;margin:0 auto;background:#fff;border:2px solid #dc2626;border-radius:8px;overflow:hidden;box-shadow:0 12px 32px rgba(220,38,38,.15)}
  .head{background:#dc2626;color:#fff;padding:16px 20px;display:flex;align-items:center;gap:12px}
  .head svg{width:32px;height:32px;flex:0 0 auto}
  .head h1{margin:0;font-size:20px;font-weight:800}
  .head small{display:block;opacity:.85;font-size:11px;margin-top:2px}
  .body{padding:18px 20px}
  .row{display:grid;grid-template-columns:120px 1fr;gap:8px;padding:8px 0;border-bottom:1px solid #f1f5f9}
  .row:last-child{border-bottom:0}
  .label{font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#64748b}
  .value{font-size:14px}
  .pill{display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;margin:2px 4px 2px 0}
  .pill.rose{background:#fee2e2;color:#7f1d1d}
  .pill.amber{background:#fef3c7;color:#78350f}
  ul{margin:2px 0 0;padding-left:18px}
  li{font-size:13px;margin:3px 0}
  a{color:#0f766e;text-decoration:underline}
  .mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
  .instr{margin:2px 0;font-size:13px}
  .foot{padding:12px 20px;background:#f8fafc;font-size:10px;color:#64748b;display:flex;justify-content:space-between;gap:12px}
  @media print{ body{background:#fff;padding:0} .card{box-shadow:none;border-width:2px} @page{margin:12mm} }
</style></head><body>
<div class="card">
  <div class="head">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
    <div><h1>Emergency Health Card</h1><small>Generated ${esc(now)} · Private device export</small></div>
  </div>
  <div class="body">${sections.join("")}</div>
  <div class="foot">
    <span>Read-only snapshot. In an emergency, call your local emergency number.</span>
    <span>TravelerPulse.pro</span>
  </div>
</div>
</body></html>`;
}

export default function EmergencyCardExport({ vault, onBack }) {
  const [data, setData]     = useState(null);
  const [picked, setPicked] = useState(() => new Set(["fullName","bloodType","allergies","primaryContact","emergencyInstructions","medicalConditions"]));

  useState(() => {
    vault.load(COMPARTMENT.HEALTH).then((d) => setData(d)).catch(() => setData(null));
  });

  const toggle = (k) => setPicked((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const download = () => {
    if (!data) return toast.error("Vault not loaded yet");
    if (picked.size === 0) return toast.error("Pick at least one field");
    const html = buildCardHtml(data, picked);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = URL.createObjectURL(blob);
    link.download = `emergency-card-${stamp}.html`;
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(link.href);
    toast.success("Emergency card downloaded");
  };

  const print = () => {
    if (!data) return toast.error("Vault not loaded yet");
    if (picked.size === 0) return toast.error("Pick at least one field");
    const html = buildCardHtml(data, picked);
    const w = window.open("", "_blank", "noopener,noreferrer,width=720,height=900");
    if (!w) return toast.error("Popup blocked — allow popups and retry");
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* noop */ } }, 250);
  };

  // Load once
  useState(() => { vault.load(COMPARTMENT.HEALTH).then(setData); });

  return (
    <div className="max-w-lg mx-auto p-4" data-testid="emergency-card-export">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" /> Back</button>

      <div className="bg-gradient-to-br from-rose-50 to-white border border-rose-100 rounded p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-rose-600 grid place-items-center"><LifeBuoy className="w-6 h-6 text-white" /></div>
          <div>
            <h1 className="font-serif font-900 text-xl">Emergency Card</h1>
            <p className="text-[11px] text-slate-500">Offline · self-contained HTML/PDF · no server</p>
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded p-4 mb-4">
        <h3 className="font-heading font-800 text-sm mb-3">Pick fields to include</h3>
        <div className="flex flex-wrap gap-1.5">
          {FIELDS.map((f) => (
            <button key={f.k} onClick={() => toggle(f.k)} data-testid={`emergency-field-${f.k}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-700 border rounded ${picked.has(f.k) ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-700 hover:border-emerald-400"}`}>
              {picked.has(f.k) && <CheckCircle2 className="w-3 h-3" />} {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button onClick={download} data-testid="emergency-download"
          className="py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-700 rounded inline-flex items-center justify-center gap-1.5">
          <FileDown className="w-4 h-4" /> Download HTML
        </button>
        <button onClick={print} data-testid="emergency-print"
          className="py-3 border-2 border-slate-300 hover:border-emerald-500 text-slate-700 font-700 rounded inline-flex items-center justify-center gap-1.5">
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
      </div>

      <div className="p-3 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span><b>How to use:</b> AirDrop / email the HTML file to your spouse or share it with a trusted contact, print a copy for your wallet or the glovebox, or save it to Files. Everything happens on this device — no server ever sees these fields.</span>
      </div>
    </div>
  );
}
